import { useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Stack,
  Checkbox,
  Typography,
  Divider,
  Skeleton,
} from "@mui/material";
// components
import { CompanyAvatar } from '../company';
import { CrewAvatar } from '../../sections/crews';
import {
  TodoCancelDialog,
} from './';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// utils
import { fKrDateDayKST, fKrDateAbsoulte } from '../../utils/formatTime';
import axios from '../../utils/axios';
import { mutate } from "swr";


export default function DoneList({crewId, dones, isActive, isValidating}) {
  const { user } = useAuth();
  const companyId = user?.employee?.company?.[0]?.id;

  const { enqueueSnackbar } = useSnackbar();
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');


  const [isOpenCancelTodo, setIsOpenCancelTodo] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  let groupDones = new Map();
  dones.forEach((done) => {
    let key = fKrDateDayKST(done.done_at);
    groupDones.has(key)
    ? groupDones.get(key).push(done)
    : groupDones.set(key, [done])
  });

  const openCancelTodo = async (todo) => {
    if(!isActive) return ;
    if(isSubmitting) return ;
    setSelectedTodo(todo);
    setIsOpenCancelTodo(true);
  }

  const closeCancelTodo = async () => {
    setIsOpenCancelTodo(false);
  }

  const clearSelection = async () => {
    setSelectedTodo({});
  }

  const cancelTodo = async () => {
    try {
      setIsSubmitting(true);
      const response = await axios.post(
        `/api/works/todo/company/${companyId}/crew/${crewId}/${selectedTodo.id}/cancel/`,
      );
      await mutate(`/api/works/todo/company/${companyId}/crew/${crewId}/`);
      mutate(`/api/works/todo/company/${companyId}/crew/${crewId}/history/`);
      enqueueSnackbar('투두를 완료 취소하였습니다.');
      closeCancelTodo();
      clearSelection();
      setIsSubmitting(false);
    } catch (err) {
      console.error(err);
      enqueueSnackbar('처리중 에러가 발생하였습니다. 관리자에 문의해 주세요.', { variant: 'error' });
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Typography variant="h6" sx={{ px: 3, pt: 2.5 }}>
        완료 데일리 투두
      </Typography>
      {isValidating && groupDones?.size === 0 && (
        <Stack sx={{ px : 3, my: 2, py: 1 }} spacing={3}>
          <Skeleton height={56} />
          <Skeleton height={56} />
          <Skeleton height={56} />
        </Stack>
      )}
      {/* e[0] : key, e[1] : value (done list) */}
      {!isValidating && groupDones?.size === 0 && (
        <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
          아직 완료한 투두가 없어요.
        </Typography>
      )}
      {[...groupDones].map((e, eindex) =>  (
        <Box key={`e-${eindex}`}>
          <Typography
            key={`e-${eindex}`}
            variant="subtitle1"
            sx={{ color: "text.disabled", px: 4, pt: 2 }}
          >
            {e[0]}
          </Typography>

          {/* pc */}
          {mdUp && e[1].map((done, index) => (
            <Stack
              key={`d-${index}`} direction="row" alignItems="center" justifyContent="space-between" spacing={5}
              sx={{
                my: 2, py: 1, px: 3,
                ':hover': {
                  backgroundColor: 'background.neutral',
                },
              }}
            >
              <Stack direction="row" alignItems="center" sx={{ width: 1 }}>
                <Checkbox
                  sx={{ mr: {xs: 2.5, sm: 3} }}
                  checked={true}
                  onClick={() => openCancelTodo(done)}
                  disabled={!isActive}
                />
                <Box sx={{ p: 0, m: 0 }}>
                <Stack>
                  <Stack display="inline-block">
                    <Typography variant="subtitle1" sx={{ textDecoration: "line-through" }}>
                      {done.content}
                    </Typography>
                  </Stack>
                </Stack>
                </Box>
              </Stack>
              <Typography sx={{ color: "text.secondary", textAlign: 'center', maxWidth: '180px', minWidth: '180px' }}>
                {fKrDateAbsoulte(done.target_date)}{done.target_date ? '까지' : ''}
              </Typography>
              {done.created_crew && (
                <CrewAvatar crewId={crewId} sx={{ maxWidth: '200px', minWidth: '200px' }} />
              )}
              {done.created_company && (
                <CompanyAvatar sx={{ maxWidth: '200px', minWidth: '200px' }} />
              )}
            </Stack>
          ))}

          {/* mo */}
          {!mdUp && e[1].map((done, index) => (
            <Stack
              key={index} direction="row" alignItems="center" spacing={1}
              sx={{
                px: 2, py: 2,
                ':active': {
                  backgroundColor: 'background.neutral',
                },
              }}
            >
              <Checkbox
                checked={true}
                onClick={() => openCancelTodo(done)}
                disabled={!isActive}
              />
              <Stack spacing={.5}>
                <Box sx={{ p: 0, m: 0 }}>
                  <Stack display="inline-block">
                    <Typography variant="subtitle1" sx={{ textDecoration: "line-through" }}>
                      {done.content}
                    </Typography>
                  </Stack>
                </Box>
                {done.created_crew && (
                  <CrewAvatar crewId={crewId} width={20} height={20} nameVariant="caption" sx={{ maxWidth: '70vw' }} />
                )}
                {done.created_company && (
                  <CompanyAvatar width={20} height={20} nameVariant="caption" sx={{ maxWidth: '70vw' }} />
                )}
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {fKrDateAbsoulte(done.target_date)}{done.target_date ? '까지' : ''}
                </Typography>
              </Stack>
            </Stack>
          ))}
          <Divider key={`ed-${eindex}`} />
        </Box>
      ))}

      <TodoCancelDialog isOpen={isOpenCancelTodo} closeDialog={closeCancelTodo} cancel={cancelTodo} isSubmitting={isSubmitting} />
    </>
  )
}
